import type { ComponentProps, ReactNode } from 'react';

import { TableRow } from '@playbook/atoms/table-row';
import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

export type TableSize = Extract<Size, 'sm' | 'md'>;

const DefaultTableSize: TableSize = 'md' as const;

type TableEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface TableDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface TableProperties extends TableEventHandlers, TableDataAttributes {
	/** Every row's height: 56px, or 36px for `small`. */
	size?: TableSize;

	/** The table's name for screen readers. */
	label: string;

	/**
	 *	Holds the first column in place at the left while the table scrolls
	 *	sideways, such as the lead or name column.
	 */
	pinStart?: boolean;
	/** Holds the last column in place at the right, such as the actions. */
	pinEnd?: boolean;

	/** A strip above the header row, such as a tab group. */
	toolbar?: ReactNode;
	/** The column headers, as `TableHeaderCell`s. */
	header: ReactNode;
	/** The rows, as `TableRow`s. */
	children: ReactNode;
	/** Below the rows, usually a `Pagination` with its `results` count. */
	footer?: ReactNode;
}

/**
 *	Draws what it is given. Sorting, selection and paging belong to the caller,
 *	which passes the rows in the order and state it wants them shown.
 */
export function Table({
	size = DefaultTableSize,

	label,

	pinStart = false,
	pinEnd = false,

	toolbar,
	header,
	children,
	footer,

	...props
}: TableProperties): ReactNode {
	return (
		<div {...props} data-size={size} className={cn(css.table, css[size])}>
			{toolbar && <div className={css.toolbar}>{toolbar}</div>}
			<div className={css.scroll}>
				<table
					aria-label={label}
					className={cn(
						css.grid,
						pinStart && css.pinStart,
						pinEnd && css.pinEnd
					)}>
					<thead className={css.head}>
						<TableRow type="header">{header}</TableRow>
					</thead>
					<tbody>{children}</tbody>
				</table>
			</div>
			{footer && <div className={css.footer}>{footer}</div>}
		</div>
	);
}
